import React, { useState, useRef } from "react";
import { FiX, FiUpload } from "react-icons/fi";
import '../styles/addTeamModal.css';

export default function AddTeamModal({ isOpen, onClose, onSave }) {
    const [teamName, setTeamName] = useState('');
    const [shortName, setShortName] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const fileInputRef = useRef(null);

    const handleClose = () => {
        if (!isSaving) {
            resetForm();
            onClose();
        }
    };

    const resetForm = () => {
        setTeamName('');
        setShortName('');
        setLogoFile(null);
        setLogoPreview('');
        setErrors({});
        setIsSaving(false);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Проверка типа файла
            if (!file.type.startsWith('image/')) {
                setErrors({ ...errors, logo: 'Пожалуйста, выберите изображение' });
                return;
            }

            // Проверка размера файла (макс 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setErrors({ ...errors, logo: 'Размер файла не должен превышать 5MB' });
                return;
            }

            setLogoFile(file);
            setErrors({ ...errors, logo: null });

            // Создаем превью
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveLogo = () => {
        setLogoFile(null);
        setLogoPreview('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!teamName.trim()) {
            newErrors.teamName = 'Название команды обязательно';
        }

        if (shortName && shortName.length > 3) {
            newErrors.shortName = 'Короткое название не должно превышать 3 буквы';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSaving(true);

        try {
            await onSave({
                name: teamName.trim(),
                shortName: shortName.trim() || null,
                logoFile: logoFile
            });
            resetForm();
        } catch (error) {
            console.error('Error saving team:', error);
            const errorMessage = error.message || 'Ошибка при сохранении команды';
            setErrors({ ...errors, submit: errorMessage });
        } finally {
            setIsSaving(false);
        }
    };

    const handleShortNameChange = (e) => {
        const value = e.target.value.toUpperCase();
        if (value.length <= 3) {
            setShortName(value);
            setErrors({ ...errors, shortName: null });
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !isSaving) {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape' && !isSaving) {
            handleClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="addTeamModalOverlay" onClick={handleClose}>
            <div className="addTeamModalContent" onClick={(e) => e.stopPropagation()}>
                <div className="addTeamModalHeader">
                    <h2 className="addTeamModalTitle">Добавить новую команду</h2>
                    <button 
                        className="addTeamModalCloseBtn" 
                        onClick={handleClose}
                        disabled={isSaving}
                    >
                        <FiX />
                    </button>
                </div>

                <div className="addTeamModalBody">
                    {/* Название команды */}
                    <div className="addTeamFormGroup">
                        <label className="addTeamFormLabel">
                            Название команды <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            className={`addTeamFormInput ${errors.teamName ? 'error' : ''}`}
                            placeholder="Введите название команды"
                            value={teamName}
                            onChange={(e) => {
                                setTeamName(e.target.value);
                                setErrors({ ...errors, teamName: null });
                            }}
                            onKeyDown={handleKeyDown}
                            disabled={isSaving}
                            autoFocus
                        />
                        {errors.teamName && (
                            <span className="addTeamFormError">{errors.teamName}</span>
                        )}
                    </div>

                    {/* Короткое название */}
                    <div className="addTeamFormGroup">
                        <label className="addTeamFormLabel">
                            Короткое название (макс. 3 буквы)
                        </label>
                        <input
                            type="text"
                            className={`addTeamFormInput ${errors.shortName ? 'error' : ''}`}
                            placeholder="ABC"
                            value={shortName}
                            onChange={handleShortNameChange}
                            onKeyDown={handleKeyDown}
                            disabled={isSaving}
                            maxLength={3}
                        />
                        {errors.shortName && (
                            <span className="addTeamFormError">{errors.shortName}</span>
                        )}
                    </div>

                    {/* Логотип */}
                    <div className="addTeamFormGroup">
                        <label className="addTeamFormLabel">Логотип команды</label>
                        
                        {!logoPreview ? (
                            <div className="addTeamLogoUpload">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    disabled={isSaving}
                                    style={{ display: 'none' }}
                                    id="logoFileInput"
                                />
                                <label 
                                    htmlFor="logoFileInput" 
                                    className={`addTeamLogoUploadLabel ${isSaving ? 'disabled' : ''}`}
                                >
                                    <FiUpload className="uploadIcon" />
                                    <span>Выберите изображение</span>
                                    <span className="uploadHint">PNG, JPG, GIF до 5MB</span>
                                </label>
                            </div>
                        ) : (
                            <div className="addTeamLogoPreview">
                                <img src={logoPreview} alt="Preview" className="addTeamLogoPreviewImg" />
                                <button 
                                    className="addTeamLogoRemoveBtn"
                                    onClick={handleRemoveLogo}
                                    disabled={isSaving}
                                >
                                    <FiX /> Удалить
                                </button>
                            </div>
                        )}
                        
                        {errors.logo && (
                            <span className="addTeamFormError">{errors.logo}</span>
                        )}
                        {!logoPreview && (
                            <span className="addTeamFormHint">
                                Если логотип не добавлен, будет использован логотип по умолчанию
                            </span>
                        )}
                    </div>

                    {errors.submit && (
                        <div className="addTeamSubmitError">{errors.submit}</div>
                    )}
                </div>

                <div className="addTeamModalFooter">
                    <button 
                        className="addTeamBtnCancel" 
                        onClick={handleClose}
                        disabled={isSaving}
                    >
                        Отмена
                    </button>
                    <button 
                        className="addTeamBtnSave" 
                        onClick={handleSave}
                        disabled={isSaving || !teamName.trim()}
                    >
                        {isSaving ? 'Сохранение...' : 'Добавить команду'}
                    </button>
                </div>
            </div>
        </div>
    );
}
